// EditableField.js
import React, { useState, useRef, useEffect } from 'react';

const EditableField = ({ value, onSubmit, className = '', textStyle = {}, inputStyle = {}, scrollOnSelect = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [lastCommittedValue, setLastCommittedValue] = useState(value);
  const holdTimer = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    setTempValue(value);
    setLastCommittedValue(value);
  }, [value]);

  useEffect(() => {
    if (!isEditing && scrollOnSelect && textRef.current) {
      const el = textRef.current;
      el.scrollLeft = 0;
      el.animate(
        [
          { transform: 'translateX(0)' },
          { transform: `translateX(-${el.scrollWidth - el.clientWidth}px)` },
          { transform: 'translateX(0)' }
        ],
        {
          duration: 6000,
          iterations: 1
        }
      );
    }
  }, [isEditing, scrollOnSelect]);

  const startLongPress = () => {
    holdTimer.current = setTimeout(() => {
      setIsEditing(true);
      setTempValue(value);
    }, 600);
  };

  const cancelLongPress = () => {
    clearTimeout(holdTimer.current);
  };

  const handleBlur = () => {
    setIsEditing(false);
    setTempValue(lastCommittedValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const trimmed = tempValue.trim();
      if (trimmed !== '' && trimmed !== value) {
        onSubmit(trimmed);
        setLastCommittedValue(trimmed);
      }
      setIsEditing(false);
    }
  };

  const baseStyle = {
    padding: '2px 6px',
    margin: '0',
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    ...textStyle
  };

  return (
    <div
      className={className}
      onMouseDown={startLongPress}
      onMouseUp={cancelLongPress}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
    >
      {isEditing ? (
        <input
          autoFocus
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{ ...baseStyle, ...inputStyle }}
        />
      ) : (
        <div ref={textRef} style={baseStyle}>{lastCommittedValue}</div>
      )}
    </div>
  );
};

export default EditableField;
